import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import {
  Button,
  Card,
  DeleteConfirmModal,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Typography,
  ProcessingLoader
} from '../../design-system';
import { ReactComponent as EmptyChartIllustration } from '../../assets/empty-chart-sw-height.svg';
import ProjectShell from '../../components/layout/ProjectShell';
import { showSuccess } from '../../components/utils/notifications';
import {
  clearSwHeightLogs,
  computeSwHeight,
  getSwHeightData,
  seedSwHeightFromSamples,
  updateSwHeightSettings
} from '../../api/data';
import {
  SW_HEIGHT_CLAMP_DEFAULTS,
  SwHeightData,
  SwHeightDatasetKind,
  WellLogs,
  SwHeightSettings,
  SwHeightSuggestedSettings,
  toComputePayload
} from '../../@types/swHeight/types';
import LogViewer, { DepthReference } from './LogViewer/LogViewer';
import SwHeightImportModal from './SwHeightImportModal';
import SwHeightFunctionPanel from './SwHeightFunctionPanel';
import SwHeightRockTypeDock from './SwHeightRockTypeDock';
import SwHeightSectionDrawers from './SwHeightSectionDrawers';
import SwHeightTable from './SwHeightTable';
import SwHeightValidationPanel from './SwHeightValidationPanel';
import {
  invalidateSwHeightCache,
  patchSwHeightCache,
  readSwHeightCache,
  writeSwHeightCache
} from './swHeightCache';
import { SwHeightSection } from './swHeightSettings';
import { useSwHeightSettingsPatch } from './useSwHeightSettingsPatch';
import styles from './SwHeightPage.module.css';

/*
 * 150ms, down from 400.
 *
 * A compute is no longer a database write, so the only cost of recomputing more
 * often is the request itself -- and the request is cheap: the compute use case
 * measures ~7ms for the 621-row example well, with a 128KB response. The debounce
 * was roughly fifty times the work it was protecting, which made it, not the
 * backend, the reason a parameter change felt slow.
 */
const COMPUTE_DEBOUNCE_MS = 150;

// One view over both domains: the log dataset is what gets calibrated, and
// the core plugs are measurements it is checked against. Writes always target
// the log dataset; core data contributes only its measured lab Sw.
const CALIBRATED_DATASET: SwHeightDatasetKind = 'log';
const MEASURED_DATASET: SwHeightDatasetKind = 'core';

const EMPTY_HINT =
  'Import an Excel workbook with a "Log Domain input Data" sheet (MD, TVDSS, ' +
  'PHIT, PERM, SWT). A "Core Domain input Data" sheet in the same file is ' +
  'loaded too, and its lab Sw is plotted against the model.';

/**
 * Stands in for the settings while no dataset is loaded, so the patch hook
 * can be called unconditionally. Module-level for a stable identity — a
 * fresh object each render would defeat the memoisation downstream. Never
 * rendered: the controls only exist once there is data to calibrate.
 */
const NO_SETTINGS: SwHeightSettings = {
  project_uid: '',
  kind: CALIBRATED_DATASET,
  fwl: null,
  grad_diff: null,
  x_axis: 'rqi',
  swirr_params: null,
  pe_params: null,
  lambda_params: null,
  best_a_params: null,
  best_b_params: null,
  true_pc_max_params: null,
  method: 'brooks_corey',
  rock_type_overrides: {},
  clamps: SW_HEIGHT_CLAMP_DEFAULTS
};

const SwHeightContent = () => {
  const location = useLocation();
  const projectUid = new URLSearchParams(location.search).get('project');

  const [swHeightData, setSwHeightData] = useState<SwHeightData | null>(null);
  /**
   * The settings last known to be stored server-side, and the baseline the
   * unsaved-changes indicator is measured against.
   *
   * `null` means nothing is stored for this project yet -- which is not the same
   * as "stored, and equal to the defaults". Without the distinction the page
   * would claim a calibration was saved when only the correlation's defaults
   * had ever been resolved.
   */
  const [savedSettings, setSavedSettings] = useState<SwHeightSettings | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  // Core rows are held for their measured lab Sw only; the core dataset's own
  // calculated Sw is never displayed.
  const [coreLogs, setCoreLogs] = useState<WellLogs | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  /**
   * Advisory notes from seeding, kept on screen because they are actionable
   * ("no well logs are imported", "import Lab Sw via Excel"). Plain
   * confirmations go to a transient toast instead -- see `showSuccess`.
   */
  const [seedWarnings, setSeedWarnings] = useState<string[]>([]);
  const [hasLogs, setHasLogs] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [activeTab, setActiveTab] = useState('domain-qc');
  // One nullable value rather than three booleans: only one section is ever
  // open, and this makes that true by construction.
  const [openSection, setOpenSection] = useState<SwHeightSection | null>(null);
  // The recompute is debounced, and the charts now sit beside the fields, so
  // the wait needs to be legible rather than shorter.
  const [computing, setComputing] = useState(false);
  const [depthReference, setDepthReference] =
    useState<DepthReference>('tvdss');
  const computeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadData = useCallback(async (uid: string) => {
    // Leaving the page and coming back should show the work as it was left,
    // unsaved edits included, rather than refetching over the top of it.
    const cached = readSwHeightCache(uid);
    if (cached) {
      setSwHeightData(cached.data);
      setCoreLogs(cached.coreLogs);
      setSavedSettings(cached.savedSettings);
      setHasLogs(cached.hasLogs);
      setLoadError(null);
      setOpenSection(null);
      return;
    }

    setLoading(true);
    setLoadError(null);
    // The drawers are keyed off state that outlives the data they edit.
    setOpenSection(null);
    // Core data is optional: absent is the normal case, not an error.
    const [calibrated, measured] = await Promise.all([
      getSwHeightData(uid, CALIBRATED_DATASET).catch(error => error as Error),
      getSwHeightData(uid, MEASURED_DATASET).catch(() => null)
    ]);

    setCoreLogs(measured ? measured.logs : null);

    if (calibrated instanceof Error) {
      const message = calibrated.message || 'Failed to load data.';
      setSwHeightData(null);
      setSavedSettings(null);
      // The backend words this per dataset ("No well logs imported").
      const missing = message.startsWith('No ') && message.includes('imported');
      if (missing) {
        setHasLogs(false);
      } else {
        setLoadError(message);
      }
      writeSwHeightCache(uid, {
        data: null,
        coreLogs: measured ? measured.logs : null,
        savedSettings: null,
        hasLogs: !missing
      });
    } else {
      setSwHeightData(calibrated);
      setHasLogs(true);
      /*
       * A first visit computes and displays a calibration that exists nowhere
       * but in this response -- the GET resolves the correlation's defaults, it
       * does not store them. Persist it once so what the user is looking at is
       * genuinely saved, which is what makes "import, then come back tomorrow"
       * work without anyone pressing anything.
       *
       * Best effort: a project reader cannot write, and that is not an error
       * they caused or can act on. They simply get no saved baseline, so the
       * unsaved-changes chip stays quiet and Save reports the real reason.
       */
      let saved: SwHeightSettings | null = calibrated.has_saved_settings
        ? calibrated.settings
        : null;
      if (!calibrated.has_saved_settings) {
        saved = await updateSwHeightSettings(
          uid,
          calibrated.settings,
          CALIBRATED_DATASET
        ).catch(() => null);
      }
      setSavedSettings(saved);
      writeSwHeightCache(uid, {
        data: calibrated,
        coreLogs: measured ? measured.logs : null,
        savedSettings: saved,
        hasLogs: true
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (projectUid) {
      loadData(projectUid);
    }
    return () => {
      if (computeTimer.current) clearTimeout(computeTimer.current);
    };
  }, [projectUid, loadData]);

  const handleSettingsChange = useCallback(
    (settings: SwHeightSettings) => {
      if (!projectUid) return;
      setSwHeightData(previous =>
        previous ? { ...previous, settings } : previous
      );
      if (computeTimer.current) clearTimeout(computeTimer.current);
      setComputing(true);
      computeTimer.current = setTimeout(async () => {
        try {
          // Every editable field has to be in the payload: the response's
          // settings replace local state, so anything omitted is echoed
          // back at its stored value and the edit reverts. `toComputePayload`
          // enumerates them so a new field cannot be forgotten here.
          const content = await computeSwHeight(
            projectUid,
            // Tuning is not saving. Every keystroke pause recomputes, and
            // while that also wrote, there was no way to tell a deliberate
            // calibration from an experiment -- nor to let a project reader
            // explore one, since persisting is what the writer check guards.
            { ...toComputePayload(settings), persist_settings: false },
            CALIBRATED_DATASET
          );
          setSwHeightData(previous => {
            const next = previous
              ? {
                  ...previous,
                  settings: content.settings,
                  result: content.result
                }
              : previous;
            if (next) patchSwHeightCache(projectUid, { data: next });
            return next;
          });
        } catch (error) {
          setLoadError(
            error instanceof Error
              ? error.message
              : 'Failed to compute Sw-height.'
          );
        } finally {
          setComputing(false);
        }
      }, COMPUTE_DEBOUNCE_MS);
    },
    [projectUid]
  );

  const handleSave = useCallback(async () => {
    if (!projectUid || !swHeightData) return;
    setSaving(true);
    setLoadError(null);
    try {
      const stored = await updateSwHeightSettings(
        projectUid,
        swHeightData.settings,
        CALIBRATED_DATASET
      );
      setSavedSettings(stored);
      patchSwHeightCache(projectUid, { savedSettings: stored });
      showSuccess('Calibration saved.');
    } catch (error) {
      // Includes the writer check: a reader gets the reason rather than a
      // button that silently does nothing.
      setLoadError(
        error instanceof Error
          ? error.message
          : 'Failed to save the calibration.'
      );
    } finally {
      setSaving(false);
    }
  }, [projectUid, swHeightData]);

  /**
   * Refetch after a failed load. The cache holds the failure too, so it has to
   * be dropped or the retry would replay it without touching the API.
   */
  const handleRetry = useCallback(async () => {
    if (!projectUid) return;
    invalidateSwHeightCache(projectUid);
    await loadData(projectUid);
  }, [projectUid, loadData]);

  /** Throw away the unsaved edits and go back to the stored calibration. */
  const handleDiscard = useCallback(async () => {
    if (!projectUid) return;
    invalidateSwHeightCache(projectUid);
    await loadData(projectUid);
  }, [projectUid, loadData]);

  const handleClear = useCallback(async () => {
    if (!projectUid) return;
    setClearing(true);
    try {
      // One view owns both datasets, so clearing removes both.
      await clearSwHeightLogs(projectUid, CALIBRATED_DATASET);
      await clearSwHeightLogs(projectUid, MEASURED_DATASET).catch(() => null);
      invalidateSwHeightCache(projectUid);
      setSwHeightData(null);
      setCoreLogs(null);
      setSavedSettings(null);
      setHasLogs(false);
      setClearOpen(false);
      setOpenSection(null);
    } catch (error) {
      setLoadError(
        error instanceof Error ? error.message : 'Failed to clear data.'
      );
    } finally {
      setClearing(false);
    }
  }, [projectUid]);

  const handleSeedFromSamples = useCallback(async () => {
    if (!projectUid) return;
    setSeeding(true);
    setLoadError(null);
    try {
      const seeded = await seedSwHeightFromSamples(
        projectUid,
        MEASURED_DATASET
      );
      showSuccess(
        `Built ${seeded.row_count} core rows from project samples.`
      );
      setSeedWarnings(seeded.warnings);
      // The stored data changed underneath the cache, so it has to be refetched
      // rather than restored.
      invalidateSwHeightCache(projectUid);
      await loadData(projectUid);
    } catch (error) {
      setLoadError(
        error instanceof Error
          ? error.message
          : 'Failed to build the dataset from project samples.'
      );
    } finally {
      setSeeding(false);
    }
  }, [projectUid, loadData]);

  const handleImported = useCallback(
    async (suggested?: SwHeightSuggestedSettings) => {
      if (!projectUid) return;
      // The user opted in to the constants found in the file: persist
      // them so the freshly imported dataset arrives calibrated.
      if (suggested && (suggested.fwl !== undefined || suggested.grad_diff !== undefined)) {
        try {
          await computeSwHeight(
            projectUid,
            {
              ...(suggested.fwl !== undefined ? { fwl: suggested.fwl } : {}),
              ...(suggested.grad_diff !== undefined
                ? { grad_diff: suggested.grad_diff }
                : {}),
              // Unlike a tuning recompute, this one is meant to stick: the user
              // opted in to the constants the file carried.
              persist_settings: true
            },
            CALIBRATED_DATASET
          );
          showSuccess(
            'Applied the calibration constants found in the imported file.'
          );
        } catch (error) {
          setLoadError(
            error instanceof Error
              ? error.message
              : 'Imported, but the calibration constants could not be applied.'
          );
        }
      }
      invalidateSwHeightCache(projectUid);
      await loadData(projectUid);
    },
    [projectUid, loadData]
  );

  // Called once, here, so the rail and all three drawers share one merge
  // path and there is exactly one `onChange(settings)` going upward. That is
  // what keeps `toComputePayload` able to derive a complete payload.
  const patchApi = useSwHeightSettingsPatch(
    swHeightData?.settings ?? NO_SETTINGS,
    handleSettingsChange
  );

  /*
   * Unsaved changes.
   *
   * Compared over `toComputePayload`, which enumerates exactly the fields that
   * get persisted -- so a field added to the settings is either persisted and
   * compared, or neither, and cannot quietly become an edit nobody can save.
   *
   * `savedSettings === null` means nothing is stored for this project (a reader,
   * or a failed first-visit save), and there is no baseline to differ from.
   */
  const isDirty = useMemo(
    () =>
      savedSettings !== null &&
      swHeightData !== null &&
      !isEqual(
        toComputePayload(swHeightData.settings),
        toComputePayload(savedSettings)
      ),
    [swHeightData, savedSettings]
  );

  /*
   * Only a reload or a tab close needs this. Navigating to another page keeps
   * the edits in the module cache, so warning there would be a prompt about
   * nothing.
   */
  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome ignores the string and shows its own wording, but still requires
      // returnValue to be set for the dialog to appear at all.
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const setOpenSectionSafely = useCallback(
    (section: SwHeightSection) => setOpenSection(section),
    []
  );
  const closeSection = useCallback(() => setOpenSection(null), []);

  // A project that never imported anything is not a failed load, even if a
  // later action (a seed, a save) happened to leave an error behind.
  const emptyIsFailure = Boolean(loadError) && hasLogs;

  const hasTvdss = Boolean(swHeightData?.logs.curves.tvdss);
  const validation = swHeightData?.result?.validation;
  const issueCount = validation
    ? validation.issues.filter(issue => issue.severity !== 'info').length
    : 0;

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Typography.Heading level={3}>
          Sw-Height Calibration
        </Typography.Heading>
        <div className={styles.toolbarActions}>
          <Select
            options={[
              { value: 'tvdss', label: 'TVDSS' },
              { value: 'md', label: 'MD' }
            ]}
            value={depthReference}
            disabled={!hasTvdss}
            onChange={value => setDepthReference(value as DepthReference)}
          />
          {isDirty && (
            <>
              <span
                className={styles.dirtyChip}
                data-testid="sw-height-unsaved-chip"
              >
                Unsaved changes
              </span>
              <Button
                variant="secondary"
                onClick={handleDiscard}
                data-testid="discard-calibration-button"
              >
                Discard changes
              </Button>
            </>
          )}
          {swHeightData && (
            <Button
              variant={isDirty ? 'primary' : 'secondary'}
              onClick={handleSave}
              loading={saving}
              disabled={!isDirty || saving}
              data-testid="save-calibration-button"
            >
              Save calibration
            </Button>
          )}
          {swHeightData && (
            <Button
              variant="secondary"
              onClick={() => setClearOpen(true)}
              data-testid="clear-data-button"
            >
              Clear data
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={handleSeedFromSamples}
            loading={seeding}
            disabled={!projectUid || seeding}
            data-testid="seed-from-samples-button"
          >
            Build core data from project samples
          </Button>
          <Button onClick={() => setImportOpen(true)} disabled={!projectUid}>
            Import data
          </Button>
        </div>
      </div>

      {/*
        * While there is data on screen the error belongs above it -- a failed
        * compute or save is about the workspace the user is looking at. With
        * nothing loaded there is no workspace to annotate, so the failure is
        * carried by the empty state instead of a bare red line on a blank page.
        */}
      {loadError && swHeightData && (
        <p className={styles.error}>{loadError}</p>
      )}
      {seedWarnings.length > 0 && (
        <div className={styles.notice} data-testid="sw-height-seed-warnings">
          <ul className={styles.noticeList}>
            {seedWarnings.map(warning => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSeedWarnings([])}
            data-testid="dismiss-seed-warnings"
          >
            Dismiss
          </Button>
        </div>
      )}

      {loading && (
        <ProcessingLoader
          title="Loading well log data"
          subtitle="Fetching the imported logs and calibrating the Sw-height model…"
          data-testid="sw-height-loading"
        />
      )}

      {/*
        * One empty state for "nothing here yet" and for "we could not load it":
        * both are the same screen with nothing on it, and the difference is
        * wording plus a retry, not a different layout. `hasLogs` is what tells
        * them apart -- a project with no import is not a failure.
        */}
      {!loading && !swHeightData && (
        <div className={styles.emptyState} data-testid="sw-height-empty">
          <EmptyChartIllustration className={styles.emptyIllustration} />
          <p className={styles.emptyTitle}>
            {emptyIsFailure
              ? 'Sw-height data unavailable'
              : 'No well logs imported'}
          </p>
          <p className={styles.emptySubtitle}>
            {emptyIsFailure ? loadError : EMPTY_HINT}
          </p>
          <div className={styles.emptyActions}>
            {emptyIsFailure && (
              <Button
                variant="secondary"
                onClick={handleRetry}
                disabled={!projectUid}
                data-testid="sw-height-retry-button"
              >
                Try again
              </Button>
            )}
            <Button onClick={() => setImportOpen(true)} disabled={!projectUid}>
              Import data
            </Button>
          </div>
        </div>
      )}

      {!loading && swHeightData && (
        <>
          {/*
           * Charts left, controls right. The calibration panel used to sit
           * above the tabs and pushed every log track below the fold, which
           * defeated the point: a parameter change is judged by watching the
           * Sw curve move. The chart column comes first in the DOM, so it is
           * also first in reading order, in tab order, and when the layout
           * collapses to one column.
           */}
          <div className={styles.topRow} data-testid="sw-height-top-row">
            {/* fullWidth is required: Card is `width: fit-content` by
                default, which here would size to the tracks' ~830px and
                overflow the grid track instead of letting them scroll. */}
            <Card
              padding="md"
              fullWidth
              className={styles.resultsColumn}
              data-testid="sw-height-results-card"
            >
              <Tabs
                value={activeTab}
                onChange={setActiveTab}
                variant="underline"
              >
                <div className={styles.tabBar}>
                  <TabList>
                    <Tab value="domain-qc">Domain QC</Tab>
                    <Tab value="table">Calculated Table</Tab>
                    <Tab value="validation">
                      {issueCount > 0
                        ? `Validation (${issueCount})`
                        : 'Validation'}
                    </Tab>
                  </TabList>
                </div>
                <TabPanels>
                  {/*
                   * keepMounted so switching tabs and back does not destroy
                   * and rebuild four Highcharts instances. It costs nothing
                   * here because this is the default tab, so it renders on
                   * first paint anyway.
                   *
                   * The other two stay destructive on purpose: SwHeightTable
                   * takes `result`, which is replaced on every compute
                   * response, so keeping it mounted would re-format hundreds
                   * of cells invisibly after every keystroke.
                   *
                   * Caveat if `activeTab` is ever put in the URL: a deep link
                   * to another tab would mount these charts while hidden
                   * (display: none, zero width) and they would stay broken.
                   * The fix then is a chart.reflow() on an
                   * IntersectionObserver, not removing keepMounted.
                   */}
                  <TabPanel value="domain-qc" keepMounted>
                    <LogViewer
                      logs={swHeightData.logs}
                      result={swHeightData.result}
                      zones={swHeightData.zones}
                      samples={swHeightData.samples}
                      depthReference={hasTvdss ? depthReference : 'md'}
                      coreMeasured={coreLogs}
                    />
                  </TabPanel>
                  <TabPanel value="table">
                    <SwHeightTable
                      logs={swHeightData.logs}
                      result={swHeightData.result}
                      settings={swHeightData.settings}
                      samples={swHeightData.samples}
                    />
                  </TabPanel>
                  <TabPanel value="validation">
                    {swHeightData.result?.validation && (
                      <SwHeightValidationPanel
                        validation={swHeightData.result.validation}
                        comparison={swHeightData.result.comparison}
                      />
                    )}
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Card>

            <div className={styles.rail} data-testid="sw-height-rail">
              <SwHeightFunctionPanel
                api={patchApi}
                openSection={openSection}
                onOpenSection={setOpenSectionSafely}
                computing={computing}
              />
            </div>
          </div>

          {/*
           * The rock-type table docks full-width under the charts rather than
           * opening in a side drawer: it is a table, and width is what it
           * needs. Sticky, so opening it does not push the tracks off screen.
           */}
          <SwHeightRockTypeDock
            open={openSection === 'rock-types'}
            onClose={closeSection}
            api={patchApi}
            zones={swHeightData.zones}
          />

          <SwHeightSectionDrawers
            openSection={openSection}
            onClose={closeSection}
            api={patchApi}
            defaults={swHeightData.defaults}
          />
        </>
      )}

      <SwHeightImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        projectUid={projectUid}
        onImported={handleImported}
      />

      <DeleteConfirmModal
        open={clearOpen}
        onClose={() => setClearOpen(false)}
        onConfirm={handleClear}
        itemType="well log and core data"
        itemName={swHeightData?.logs.source_filename ?? 'imported data'}
        loading={clearing}
      />
    </div>
  );
};

const SwHeightPage = () => (
  <ProjectShell>
    <SwHeightContent />
  </ProjectShell>
);

export default SwHeightPage;
